import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CabinetShell from '../orders/CabinetShell';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { FileText, FolderOpen, Loader2, Receipt, Search, ShieldCheck } from 'lucide-react';
import { statusMeta, fmtDate } from '../orders/orderConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ icon: Icon, title, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-2">
      <Icon className="h-3.5 w-3.5" />{title}
    </p>
    <div className="space-y-2">{children}</div>
  </div>
);

export default function CabinetSearch() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/search`, { params: { q: q.trim() } });
        setResults(data);
        setParams({ q: q.trim() }, { replace: true });
      } catch (e) { setResults(null); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q, setParams]);

  const empty = results && !results.orders.length && !results.client_invoices.length
    && !results.certificates.length && !results.files.length;

  return (
    <CabinetShell title="Recherche">
      <div className="space-y-5" data-testid="cabinet-search">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="N° JPD, patient, praticien, type de prothèse, facture, document..."
            className="pl-10 h-12 text-base" data-testid="cabinet-search-input" />
          {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        {q.trim().length < 2 && (
          <p className="text-sm text-muted-foreground text-center py-6">
            Tapez au moins 2 caractères pour rechercher dans vos dossiers, factures et documents.
          </p>
        )}
        {empty && <p className="text-sm text-muted-foreground text-center py-6" data-testid="cabinet-search-empty">Aucun résultat pour « {q} ».</p>}

        {results?.orders.length > 0 && (
          <Section icon={FileText} title={`Dossiers (${results.orders.length})`}>
            {results.orders.map((o) => {
              const m = statusMeta(o.status);
              return (
                <Link key={o.id} to={`/orders/${o.id}`} className="block" data-testid={`search-order-${o.order_number}`}>
                  <Card className="border-border hover:border-primary/50 transition-colors">
                    <CardContent className="py-3 flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <p className="font-mono font-semibold text-sm">{o.order_number}</p>
                        <p className="text-xs text-muted-foreground">
                          {o.patient_last_name || o.patient_ref} · {o.praticien_name} · {o.product_label}
                        </p>
                      </div>
                      <Badge variant="outline" className={m.cls}>{m.label}</Badge>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </Section>
        )}

        {results?.files.length > 0 && (
          <Section icon={FolderOpen} title={`Documents (${results.files.length})`}>
            {results.files.map((f) => (
              <Link key={f.id} to={`/orders/${f.order_id}`} className="block" data-testid={`search-file-${f.id}`}>
                <Card className="border-border hover:border-primary/50 transition-colors">
                  <CardContent className="py-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{f.original_name}</p>
                      <p className="text-xs text-muted-foreground">{f.order_number} · {fmtDate(f.uploaded_at)}</p>
                    </div>
                    {f.purged && <Badge variant="outline" className="text-gray-500">Expiré</Badge>}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </Section>
        )}

        {results?.client_invoices.length > 0 && (
          <Section icon={Receipt} title={`Factures (${results.client_invoices.length})`}>
            {results.client_invoices.map((inv) => (
              <Link key={inv.id} to="/invoices" className="block" data-testid={`search-invoice-${inv.invoice_number}`}>
                <Card className="border-border hover:border-primary/50 transition-colors">
                  <CardContent className="py-3 flex items-center justify-between">
                    <p className="text-sm font-semibold">{inv.invoice_number}</p>
                    <p className="text-sm">{inv.total?.toFixed(2)} €</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </Section>
        )}

        {results?.certificates.length > 0 && (
          <Section icon={ShieldCheck} title={`Certificats (${results.certificates.length})`}>
            {results.certificates.map((c) => (
              <Link key={c.order_id} to={`/orders/${c.order_id}`} className="block" data-testid={`search-cert-${c.order_number}`}>
                <Card className="border-border hover:border-primary/50 transition-colors">
                  <CardContent className="py-3 flex items-center justify-between">
                    <p className="text-sm font-medium">Certificat de conformité — {c.order_number}</p>
                    <p className="text-xs text-muted-foreground">{fmtDate(c.signed_at)}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </Section>
        )}
      </div>
    </CabinetShell>
  );
}
